import { extendTheme, type ThemeConfig } from '@chakra-ui/react';
import { CompanyId } from '../Config/CompanyConfig';

const config: ThemeConfig = {
  initialColorMode: 'system',
  useSystemColorMode: true,
};

// -------------------------------------------------------------
// Eko Power Limited Brand Color Palette
// -------------------------------------------------------------
const ekoPowerColors = {
  brand: {
    50: '#EEF4FF',
    100: '#D9E6FE',
    200: '#BCD3FD',
    300: '#8EBAFB',
    400: '#5A99F7',
    500: '#1565C0', // Core Royal Blue
    600: '#0E4DA5',
    700: '#0A3B82',
    800: '#072B62',
    900: '#051E48', // Deepest Brand Blue
  },
  accentCyan: {
    50: '#E0F7FE',
    100: '#B9EEFD',
    200: '#7CE0FB',
    300: '#38CEF7',
    400: '#00B4D8',
    500: '#0096C7',
    600: '#0077B6',
  },
  accentYellow: {
    50: '#FFFBEA',
    100: '#FFF3C4',
    200: '#FFE588',
    300: '#FFD34D',
    400: '#FFC11A',
    500: '#FFB703', // Logo Yellow
    600: '#E09C00',
  },
  accentOrange: {
    50: '#FFF1E8',
    100: '#FEDCC7',
    200: '#FDBB95',
    300: '#FB945E',
    400: '#F77028',
    500: '#F35B04', // Logo Orange
    600: '#CC4600',
  },
  navy: {
    500: '#0D1F4C',
    600: '#0A183C',
    700: '#07122E',
    800: '#050D21',
    900: '#030816',
  },
  surface: {
    light: '#FFFFFF',
    lightSubtle: '#F6F8FC',
    lightBorder: 'rgba(13, 31, 76, 0.08)',
    dark: '#0D1527',
    darkSubtle: '#121C34',
    darkBorder: 'rgba(255, 255, 255, 0.08)',
  },
};

// -------------------------------------------------------------
// EKEDP (Eko Electricity Distribution PLC) Brand Color Palette
// Derived from the official approved logo (Navy, Red, Yellow)
// -------------------------------------------------------------
const ekedpColors = {
  brand: {
    50: '#F3F2F9',
    100: '#E2E0F1',
    200: '#C3C0E2',
    300: '#9F9ACF',
    400: '#6E66B8',
    500: '#312E60', // Core EKEDP Deep Indigo Navy from Logo
    600: '#282552',
    700: '#1F1D42',
    800: '#161432',
    900: '#0F0E23', // Deep Midnight Indigo
  },
  accentCyan: {
    50: '#EEF2FF',
    100: '#E0E7FF',
    200: '#C7D2FE',
    300: '#A5B4FC',
    400: '#818CF8',
    500: '#6366F1', // Electric Indigo
    600: '#4F46E5',
  },
  accentYellow: {
    50: '#FFFDEB',
    100: '#FFFAC6',
    200: '#FFF492',
    300: '#FFED5E',
    400: '#FFE536',
    500: '#FFDC21', // EKEDP Lightning Bolt Yellow from Logo
    600: '#E5BE0A',
    700: '#B89600',
  },
  accentOrange: {
    50: '#FEECEE',
    100: '#FCD0D3',
    200: '#F9A4AA',
    300: '#F4717C',
    400: '#F04351',
    500: '#EC3138', // EKEDP Red from Logo
    600: '#D41F26',
  },
  accentRed: {
    50: '#FEECEE',
    100: '#FCD0D3',
    200: '#F9A4AA',
    300: '#F4717C',
    400: '#F04351',
    500: '#EC3138', // EKEDP Red from Logo
    600: '#D41F26',
    700: '#B0151B',
    800: '#8A1116',
    900: '#650B0F',
  },
  navy: {
    500: '#1C1844', // EKEDP Dark Navy
    600: '#171339',
    700: '#120F2F',
    800: '#0D0A23',
    900: '#080617',
  },
  surface: {
    light: '#FFFFFF',
    lightSubtle: '#F6F5FB',
    lightBorder: 'rgba(49, 46, 96, 0.08)',
    dark: '#13102C',
    darkSubtle: '#1A163B',
    darkBorder: 'rgba(255, 255, 255, 0.08)',
  },
};

export const getCompanyTheme = (companyId: CompanyId = 'ekopower') => {
  const isEkedp = companyId === 'ekedp';
  const colors = isEkedp ? ekedpColors : ekoPowerColors;

  return extendTheme({
    config,
    fonts: {
      heading: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      accent: "'Cormorant Garamond', Georgia, serif",
    },
    colors,
    styles: {
      global: (props: any) => ({
        body: {
          bg: props.colorMode === 'dark' 
            ? (isEkedp ? '#0B091B' : '#070C18') 
            : (isEkedp ? '#F6F5FB' : '#F4F6FB'),
          color: props.colorMode === 'dark' ? '#F1F5F9' : '#0F172A',
          transition: 'background-color 0.25s ease, color 0.25s ease',
          overflowX: 'hidden',
        },
        '*::placeholder': {
          color: props.colorMode === 'dark' ? 'gray.500' : 'gray.400',
        },
        '*, *::before, &::after': {
          borderColor: props.colorMode === 'dark' 
            ? 'rgba(255, 255, 255, 0.08)' 
            : (isEkedp ? 'rgba(49, 46, 96, 0.08)' : 'rgba(13, 31, 76, 0.08)'),
        },
      }),
    },
    components: {
      Button: {
        baseStyle: {
          fontWeight: '600',
          borderRadius: '10px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
      Card: {
        baseStyle: (props: any) => ({
          container: {
            bg: props.colorMode === 'dark' 
              ? (isEkedp ? '#13102C' : '#0D1527') 
              : '#FFFFFF',
            borderRadius: '16px',
            borderWidth: '1px',
            borderColor: props.colorMode === 'dark' 
              ? 'rgba(255, 255, 255, 0.08)' 
              : (isEkedp ? 'rgba(49, 46, 96, 0.08)' : 'rgba(13, 31, 76, 0.08)'),
            boxShadow: props.colorMode === 'dark' 
              ? '0 10px 30px -10px rgba(0, 0, 0, 0.5)' 
              : (isEkedp 
                  ? '0 10px 30px -10px rgba(49, 46, 96, 0.08)' 
                  : '0 10px 30px -10px rgba(13, 31, 76, 0.08)'),
          },
        }),
      },
    },
  });
};

const defaultTheme = getCompanyTheme('ekopower');
export default defaultTheme;